import { useSearch } from "@tanstack/react-router"
import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useCallback, useEffect, useMemo, useRef } from "react"

import { type CostBreakdownGoodsReceiptsSearchFilters, useCostBreakdownGoodsReceiptsSearchStore } from "../stores"

import type { GoodsReceipt, GoodsReceiptSortField } from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus as GRStatus } from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import {
  type WarehouseReceivingTableSearchParams,
  type WarehouseReceivingTableStoreSelectors,
  useWarehouseReceivingTableBase,
} from "@/screens/WarehouseReceiving/hooks/useWarehouseReceivingTableBase"
import type { Option } from "@/types/form-fields"

interface UseCostBreakdownTableReturn {
  goodsReceipts: GoodsReceipt[]
  isGoodsReceiptsFetching: boolean
  searchQuery: string
  warehouseAreaIds: string[]
  warehouseOptions: Option<string>[]
  isWarehousesFetching: boolean
  expandedState: ExpandedState
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  handleSearchChange: (query: string) => void
  handleFilterChange: (warehouseIds: string[]) => void
  handleFilterRemove: (warehouseId: string) => void
  handleFilterClear: () => void
  handleExpandedChange: OnChangeFn<ExpandedState>
  handleSortingChange: OnChangeFn<SortingState>
  handleScroll: () => void
  handleRefetch: () => void
}

export const useCostBreakdownTable = (): UseCostBreakdownTableReturn => {
  const searchParams = useSearch({
    from: "/app/procurement/_layout/cost-breakdown",
    strict: true,
  })

  const data = useCostBreakdownGoodsReceiptsSearchStore((state) => state.data)
  const fetching = useCostBreakdownGoodsReceiptsSearchStore((state) => state.fetching)
  const executeSearch = useCostBreakdownGoodsReceiptsSearchStore((state) => state.executeSearch)
  const loadMore = useCostBreakdownGoodsReceiptsSearchStore((state) => state.loadMore)
  const clearStore = useCostBreakdownGoodsReceiptsSearchStore((state) => state.clearStore)
  const pageInfo = useCostBreakdownGoodsReceiptsSearchStore((state) => state.pageInfo)

  const reactiveStoreSelectors: WarehouseReceivingTableStoreSelectors<
    GoodsReceipt,
    CostBreakdownGoodsReceiptsSearchFilters
  > = useMemo(
    () => ({
      data,
      fetching,
      executeSearch: executeSearch as any,
      loadMore: loadMore as any,
      clearStore,
      pageInfo,
    }),
    [data, fetching, executeSearch, loadMore, clearStore, pageInfo]
  )

  const buildSearchParams = useCallback(
    (baseParams: WarehouseReceivingTableSearchParams): CostBreakdownGoodsReceiptsSearchFilters => {
      const isProductCostCompleted = searchParams.isProductCostCompleted

      return {
        query: baseParams.query,
        warehouseAreaIds: baseParams.warehouseAreaIds,
        field: baseParams.field as GoodsReceiptSortField,
        order: baseParams.order,
        statuses: [GRStatus.CheckedIn, GRStatus.Stored],
        isProductCostCompleted,
      }
    },
    [searchParams.isProductCostCompleted]
  )

  const baseHookReturn = useWarehouseReceivingTableBase<
    GoodsReceipt,
    GoodsReceiptSortField,
    CostBreakdownGoodsReceiptsSearchFilters
  >({
    store: reactiveStoreSelectors,
    searchParamsRoute: "/app/procurement/_layout/cost-breakdown",
    navigationRoute: "/app/procurement/cost-breakdown",
    buildSearchParams,
    isExpandable: true,
    queryClient,
  })

  const handleExpandedChange = baseHookReturn.handleExpandedChange
  const autoExpandedReceiptIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!searchParams.query || !data.length || fetching) {
      autoExpandedReceiptIdRef.current = null
      return
    }

    const matchingReceipt = data.find((receipt) => receipt.agrNumber === searchParams.query)

    if (
      !matchingReceipt ||
      !handleExpandedChange ||
      autoExpandedReceiptIdRef.current === matchingReceipt.goodsReceiptId
    ) {
      return
    }

    autoExpandedReceiptIdRef.current = matchingReceipt.goodsReceiptId
    handleExpandedChange((prevExpandedState) =>
      typeof prevExpandedState === "boolean"
        ? prevExpandedState
        : { ...prevExpandedState, [matchingReceipt.goodsReceiptId]: true }
    )
  }, [data, searchParams.query, fetching, handleExpandedChange])

  return {
    goodsReceipts: baseHookReturn.data,
    isGoodsReceiptsFetching: baseHookReturn.isFetching,
    searchQuery: baseHookReturn.searchQuery,
    warehouseAreaIds: baseHookReturn.warehouseAreaIds,
    warehouseOptions: baseHookReturn.warehouseOptions,
    isWarehousesFetching: baseHookReturn.isWarehousesFetching,
    expandedState: baseHookReturn.expandedState!,
    sortingState: baseHookReturn.sortingState,
    tableContainerRef: baseHookReturn.tableContainerRef,
    handleSearchChange: baseHookReturn.handleSearchChange,
    handleFilterChange: baseHookReturn.handleFilterChange,
    handleFilterRemove: baseHookReturn.handleFilterRemove,
    handleFilterClear: baseHookReturn.handleFilterClear,
    handleExpandedChange: baseHookReturn.handleExpandedChange!,
    handleSortingChange: baseHookReturn.handleSortingChange,
    handleScroll: baseHookReturn.handleScroll,
    handleRefetch: baseHookReturn.handleRefetch,
  }
}
